import ClassNames from "classnames";
import { EmblaCarouselType } from "embla-carousel";
import { FunctionComponent, MouseEvent, useEffect, useState } from "react";
import {
  ColorScheme,
  ContainerQueries,
  ImageModuleSettings,
  ImagesModuleSettings,
  Language,
  OverlayModuleSettings,
  TranslatedModule,
} from "../types/index.js";
import { getTranslations } from "../utils/utils.js";
import Icon from "./Icon.js";
import ImageModule from "./Modules/ImageModule.js";
import { getBreakpointImageColumnsCount } from "./Modules/ImagesModule.js";
import OverlayModule from "./Modules/OverlayModule.js";
import Slider from "./Slider.js";

interface Props {
  pageId: string;
  images: TranslatedModule<ImageModuleSettings>[];
  isPreview: boolean;
  isInsideHeader: boolean;
  scheme: ColorScheme;
  translatedModule: TranslatedModule<ImagesModuleSettings>;
  overlayModule: TranslatedModule<OverlayModuleSettings> | undefined;
  activeModuleId: string | undefined;
  slidesToShow: number;
  queries: ContainerQueries;
}

const translations = {
  de: { prev: "vorheriges Bild anzeigen", next: "nächstes Bild anzeigen" },
  it: {
    prev: "mostrare immagine precedente",
    next: "mostrare immagine successiva",
  },
  en: { prev: "show previous image", next: "show next image" },
  fr: {
    prev: "afficher l’image précédente",
    next: "afficher l’image suivante",
  },
};

type ArrowComponent = FunctionComponent<{
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  languageId: Language;
}>;

const PrevArrow: ArrowComponent = ({ onClick, languageId }) => {
  const i18n = getTranslations(languageId, translations);
  return (
    <button
      onClick={onClick}
      className="ContentSlider__Control--prev ContentSlider__Control"
      aria-label={i18n.prev}
    >
      <Icon className="ContentSlider__Control__Icon" glyph="arrow-left" />
    </button>
  );
};

const NextArrow: ArrowComponent = ({ onClick, languageId }) => {
  const i18n = getTranslations(languageId, translations);
  return (
    <button
      onClick={onClick}
      className="ContentSlider__Control--next ContentSlider__Control"
      aria-label={i18n.next}
    >
      <Icon className="ContentSlider__Control__Icon" glyph="arrow-right" />
    </button>
  );
};

const ImageSlider: FunctionComponent<Props> = ({
  images,
  isPreview,
  translatedModule: {
    settings: { mediaAspectRatio, sliderEffect, columnsCount, gap },
    translation: { languageId },
  },
  scheme,
  pageId,
  overlayModule,
  activeModuleId,
  slidesToShow: slidesToShowProp,
  isInsideHeader,
  queries,
}) => {
  const [slider, setSlider] = useState<EmblaCarouselType>();
  const [maxSelectedIndex, setMaxSelectedIndex] = useState(0);
  const [currentIndexes, setCurrentIndexes] = useState([0, 1]);
  const slidesToShow =
    sliderEffect === "fade"
      ? 1
      : getBreakpointImageColumnsCount(queries, slidesToShowProp);

  const showArrows = images.length > slidesToShow;

  useEffect(() => {
    slider?.on("select", () => {
      const currentIndex = slider?.selectedScrollSnap() ?? 0;
      setMaxSelectedIndex((maxSelectedIndex) =>
        currentIndex > maxSelectedIndex ? currentIndex : maxSelectedIndex,
      );
      setCurrentIndexes([currentIndex - 1, currentIndex, currentIndex + 1]);
    });
  }, [slider]);

  return (
    <div className="SliderModule">
      {showArrows && (
        <PrevArrow
          languageId={languageId}
          onClick={() => {
            if (!slider) return;
            const slidesCount = slider.slideNodes().length;
            slider.canScrollPrev()
              ? slider.scrollPrev()
              : slider.scrollTo(slidesCount);
          }}
        />
      )}
      <Slider
        containerClassName={ClassNames("SliderModule__Container", {
          [`SliderModule__Container--gap-${gap}`]: sliderEffect === "slide",
        })}
        slideClassName="SliderModule__SlideOuter"
        isPreview={isPreview}
        onSliderLoaded={setSlider}
        slidesToShow={slidesToShow}
        sliderEffect={sliderEffect}
        activePreviewSlideId={activeModuleId}
        slideIds={images.map(({ id }) => id)}
      >
        {images.map((image, index) => (
          <div className="SliderModule__Slide" key={image.id}>
            {(index <= maxSelectedIndex + columnsCount ||
              sliderEffect === "slide") && (
              <ImageModule
                key={image.id}
                aspectRatio={mediaAspectRatio}
                translatedModule={image}
                width={1920}
                lazyLoad={
                  !(
                    (index === 0 && isInsideHeader) ||
                    currentIndexes.includes(index)
                  )
                }
                sizes="100vw"
                isPreview={isPreview}
                isSlider={true}
                pageId={pageId}
                scheme={scheme}
                showOverlay={!overlayModule}
              />
            )}
          </div>
        ))}
      </Slider>
      {showArrows && (
        <NextArrow
          languageId={languageId}
          onClick={() => {
            slider?.canScrollNext()
              ? slider?.scrollNext()
              : slider?.scrollTo(0);
          }}
        />
      )}

      {overlayModule && (
        <OverlayModule
          isPreview={isPreview}
          scheme={scheme}
          isSlider={true}
          translatedModule={overlayModule}
          pageId={pageId}
        />
      )}
    </div>
  );
};

export default ImageSlider;
